<script lang="ts">
  import { onMount } from 'svelte';
  import AppShell from '../components/AppShell.svelte';
  import StatusBadge from '../components/StatusBadge.svelte';
  import Avatar from '../components/Avatar.svelte';
  import { apiFetch } from '../lib/api';
  import { fmtDate, fmtInt, fmtUah } from '../lib/format';
  import { fetchPublicDataTable, type ApiField, type ApiFieldGroup } from '../lib/data-tables-api';
  import { updatePayment } from '../lib/admin-api';
  import { session } from '../lib/session';
  import InlineFieldEdit from '../lib/fields/InlineFieldEdit.svelte';
  import ReverseLinks from '../lib/fields/ReverseLinks.svelte';

  interface PaymentDetail {
    id: string;
    occurredAt: string;
    requestedAt: string | null;
    refundedAt: string | null;
    chargebackAt: string | null;
    importedAt: string | null;
    amountCents: number;
    currency: string;
    authAmountCents: number | null;
    authCurrency: string | null;
    feeCents: number | null;
    netCents: number | null;
    refundAmountCents: number | null;
    status: string;
    rawStatus: string | null;
    externalRef: string | null;
    processorPaymentId: string | null;
    provider: string;
    merchantName: string | null;
    merchantId: string | null;
    paymentMethod: string | null;
    cardBrand: string | null;
    cardLast4: string | null;
    cardBin: string | null;
    installmentParts: number | null;
    rawTariff: string | null;
    productSku: string | null;
    productName: string | null;
    payerEmail: string | null;
    payerPhone: string | null;
    payerFirstName: string | null;
    payerLastName: string | null;
    payerCountry: string | null;
    payerIp: string | null;
    errorCode: string | null;
    errorMessage: string | null;
    refundReason: string | null;
    importedFrom: string | null;
    inferred: boolean;
    rawData: Record<string, unknown> | null;
    meta: Record<string, unknown> | null;
    customFields: Record<string, unknown>;
    person: {
      id: string;
      displayName: string | null;
      primaryEmail: string | null;
      primaryPhone: string | null;
      ltvCents: number;
      paymentCount: number;
    } | null;
  }

  let { params }: { params: { id?: string } } = $props();
  const id = $derived(params?.id ?? '');

  let p = $state<PaymentDetail | null>(null);
  let loading = $state(true);
  let err = $state<string | null>(null);
  let showRaw = $state(false);

  /** Усі поля catalog "payments" — для schema-driven sections у sidebar. */
  let customFieldDefs = $state<ApiField[]>([]);
  let allGroups = $state<ApiFieldGroup[]>([]);
  /** Згруповані custom-поля (без built-in — built-in рендеряться у main grid).
   *  Sort: CustomFieldGroup.position для named, "Додаткові" для null group у кінці. */
  const groupedCustomFields = $derived.by<{ label: string; fields: ApiField[] }[]>(() => {
    if (customFieldDefs.length === 0) return [];
    const map = new Map<string, ApiField[]>();
    for (const f of customFieldDefs) {
      const key = f.groupName || 'Додаткові';
      const arr = map.get(key) ?? [];
      arr.push(f);
      map.set(key, arr);
    }
    for (const [, arr] of map) {
      arr.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    }
    const groupPos = new Map<string, number>();
    for (const g of allGroups) groupPos.set(g.name, g.position);
    const named = Array.from(map.entries())
      .filter(([k]) => k !== 'Додаткові')
      .sort(([a], [b]) => {
        const pa = groupPos.get(a) ?? 9999;
        const pb = groupPos.get(b) ?? 9999;
        if (pa !== pb) return pa - pb;
        return a.localeCompare(b);
      });
    const result = named.map(([label, fields]) => ({ label, fields }));
    if (map.has('Додаткові')) {
      result.push({ label: 'Додаткові', fields: map.get('Додаткові')! });
    }
    return result;
  });
  const isAdmin = $derived($session?.role === 'ADMIN');

  onMount(async () => {
    if (!id) {
      err = 'No payment id';
      loading = false;
      return;
    }
    try {
      const [payment, schema] = await Promise.all([
        apiFetch<PaymentDetail>(`/payments/${id}`),
        fetchPublicDataTable('payments').catch(() => null),
      ]);
      p = payment;
      customFieldDefs = (schema?.fields ?? []).filter((f) => !f.builtIn && !f.hidden);
      allGroups = schema?.groups ?? [];
    } catch (e) {
      err = e instanceof Error ? e.message : String(e);
    } finally {
      loading = false;
    }
  });

  async function refresh(): Promise<void> {
    if (!id) return;
    p = await apiFetch<PaymentDetail>(`/payments/${id}`);
  }

  async function saveTypedCustomField(fieldKey: string, next: unknown): Promise<void> {
    const v = next === '' || next === undefined ? null : next;
    await updatePayment(id, { customFields: { [fieldKey]: v } });
    await refresh();
  }

  const payerName = $derived(p ? [p.payerFirstName, p.payerLastName].filter(Boolean).join(' ') : '');
  const isInstallment = $derived(p?.installmentParts && p.installmentParts > 0);
  const isRefund = $derived(p?.status === 'REFUNDED');
  const cardDisplay = $derived(
    p?.cardBrand || p?.cardLast4
      ? `${p.cardBrand ?? 'card'} •••• ${p.cardLast4 ?? '••••'}`
      : null,
  );

  function fmtDateTime(iso: string | null): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('uk-UA', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }
</script>

<AppShell active="payments">
  <div class="mx-auto max-w-[1200px]">
    <div class="text-xs text-[var(--color-text-muted)] mb-2">
      <a href="#/payments" class="text-[var(--color-pu)] no-underline hover:underline">Платежі</a> ›
      <span>{p?.externalRef ?? id.slice(0, 8)}</span>
    </div>

    {#if loading}
      <div class="py-20 text-center text-sm text-[var(--color-text-muted)]">Завантаження…</div>
    {:else if err}
      <div class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{err}</div>
    {:else if p}
      <!-- HEADER -->
      <header class="mb-5">
        <div class="flex items-start gap-3 mb-1">
          <h1 class="text-2xl font-bold leading-tight">
            {isRefund ? '–' : ''}{fmtUah(p.amountCents)} <span class="text-[var(--color-text-muted)] text-base font-medium">{p.currency}</span>
          </h1>
          <StatusBadge status={p.status} />
          {#if p.inferred}<span class="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold text-blue-700">inferred</span>{/if}
          {#if isInstallment}<span class="rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-bold text-purple-700">розстрочка {p.installmentParts} платежів</span>{/if}
        </div>
        <p class="text-[12px] text-[var(--color-text-muted)]">
          Order: <code>{p.externalRef ?? '—'}</code> · {fmtDateTime(p.occurredAt)} · provider: <strong>{p.provider}</strong>
        </p>
      </header>

      <!-- LAYOUT: main + sidebar -->
      <div class="grid grid-cols-[1fr_280px] gap-5">
        <div class="space-y-4">
          <!-- AMOUNTS -->
          <section class="rounded-xl border border-[var(--color-border-3)] bg-white p-5">
            <div class="text-[13px] font-bold mb-3">Сума</div>
            <div class="grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
              <div class="flex justify-between"><span class="text-[var(--color-text-muted)]">Brutto</span><strong class="tabular-nums">{fmtUah(p.amountCents)} {p.currency}</strong></div>
              <div class="flex justify-between"><span class="text-[var(--color-text-muted)]">Авторизація</span><strong class="tabular-nums">{p.authAmountCents != null ? fmtUah(p.authAmountCents) + ' ' + (p.authCurrency ?? '—') : '—'}</strong></div>
              <div class="flex justify-between"><span class="text-[var(--color-text-muted)]">Комісія</span><strong class="tabular-nums">{p.feeCents != null ? fmtUah(p.feeCents) : '—'}</strong></div>
              <div class="flex justify-between"><span class="text-[var(--color-text-muted)]">Net</span><strong class="tabular-nums">{p.netCents != null ? fmtUah(p.netCents) : (p.feeCents != null ? fmtUah(p.amountCents - p.feeCents) : '—')}</strong></div>
              {#if p.refundAmountCents != null}
                <div class="flex justify-between col-span-2 pt-2 border-t border-[var(--color-border-3)]">
                  <span class="text-red-700">Refund amount</span>
                  <strong class="text-red-700 tabular-nums">{fmtUah(p.refundAmountCents)}</strong>
                </div>
              {/if}
            </div>
          </section>

          <!-- TIMELINE -->
          <section class="rounded-xl border border-[var(--color-border-3)] bg-white p-5">
            <div class="text-[13px] font-bold mb-3">Хронологія</div>
            <div class="space-y-2 text-xs">
              <div class="flex justify-between"><span class="text-[var(--color-text-muted)]">Запит ініційовано</span><span class="tabular-nums font-mono text-[11px]">{fmtDateTime(p.requestedAt)}</span></div>
              <div class="flex justify-between"><span class="text-[var(--color-text-muted)]">Платіж проведено</span><span class="tabular-nums font-mono text-[11px]">{fmtDateTime(p.occurredAt)}</span></div>
              {#if p.refundedAt}
                <div class="flex justify-between text-red-700"><span>Refunded</span><span class="tabular-nums font-mono text-[11px]">{fmtDateTime(p.refundedAt)}</span></div>
              {/if}
              {#if p.chargebackAt}
                <div class="flex justify-between text-red-700"><span>Chargeback</span><span class="tabular-nums font-mono text-[11px]">{fmtDateTime(p.chargebackAt)}</span></div>
              {/if}
              {#if p.importedAt}
                <div class="flex justify-between text-[var(--color-text-muted)]"><span>Імпортовано в систему</span><span class="tabular-nums font-mono text-[11px]">{fmtDateTime(p.importedAt)}</span></div>
              {/if}
            </div>
          </section>

          <!-- PAYMENT METHOD -->
          <section class="rounded-xl border border-[var(--color-border-3)] bg-white p-5">
            <div class="text-[13px] font-bold mb-3">Спосіб оплати</div>
            <div class="grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
              <div class="flex justify-between"><span class="text-[var(--color-text-muted)]">Метод</span><strong>{p.paymentMethod ?? '—'}</strong></div>
              <div class="flex justify-between"><span class="text-[var(--color-text-muted)]">Картка</span><strong>{cardDisplay ?? '—'}</strong></div>
              {#if p.cardBin}<div class="flex justify-between"><span class="text-[var(--color-text-muted)]">BIN</span><strong class="font-mono">{p.cardBin}</strong></div>{/if}
              {#if p.installmentParts}<div class="flex justify-between"><span class="text-[var(--color-text-muted)]">Частин</span><strong>{p.installmentParts}</strong></div>{/if}
            </div>
          </section>

          <!-- PAYER -->
          <section class="rounded-xl border border-[var(--color-border-3)] bg-white p-5">
            <div class="text-[13px] font-bold mb-3">Платник (з CSV)</div>
            <div class="grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
              <div class="flex justify-between"><span class="text-[var(--color-text-muted)]">Ім'я</span><strong>{payerName || '—'}</strong></div>
              <div class="flex justify-between"><span class="text-[var(--color-text-muted)]">Країна</span><strong>{p.payerCountry ?? '—'}</strong></div>
              <div class="flex justify-between"><span class="text-[var(--color-text-muted)]">Email</span><strong class="truncate">{p.payerEmail ?? '—'}</strong></div>
              <div class="flex justify-between"><span class="text-[var(--color-text-muted)]">Телефон</span><strong>{p.payerPhone ?? '—'}</strong></div>
              {#if p.payerIp}<div class="flex justify-between col-span-2"><span class="text-[var(--color-text-muted)]">IP</span><strong class="font-mono text-[11px]">{p.payerIp}</strong></div>{/if}
            </div>
          </section>

          <!-- PRODUCT / ITEMS -->
          <section class="rounded-xl border border-[var(--color-border-3)] bg-white p-5">
            <div class="text-[13px] font-bold mb-3">Товари / зіставлення</div>
            <div class="grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
              <div class="flex justify-between col-span-2"><span class="text-[var(--color-text-muted)]">Raw tariff (з CSV)</span><strong class="font-mono">{p.rawTariff ?? '—'}</strong></div>
              <div class="flex justify-between"><span class="text-[var(--color-text-muted)]">Product SKU</span>
                {#if p.productSku}<strong class="font-mono">{p.productSku}</strong>{:else}<span class="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold text-blue-700">unmatched</span>{/if}
              </div>
              <div class="flex justify-between"><span class="text-[var(--color-text-muted)]">Product name</span><strong>{p.productName ?? '—'}</strong></div>
            </div>
          </section>

          <!-- ERRORS -->
          {#if p.errorCode || p.errorMessage || p.refundReason}
            <section class="rounded-xl border border-red-200 bg-red-50 p-5">
              <div class="text-[13px] font-bold mb-3 text-red-800">Помилки / Причини</div>
              <div class="space-y-2 text-xs text-red-700">
                {#if p.errorCode}<div class="flex justify-between"><span>Error code</span><strong class="font-mono">{p.errorCode}</strong></div>{/if}
                {#if p.errorMessage}<div><span class="text-red-600">Error message:</span> <strong>{p.errorMessage}</strong></div>{/if}
                {#if p.refundReason}<div><span class="text-red-600">Refund reason:</span> <strong>{p.refundReason}</strong></div>{/if}
              </div>
            </section>
          {/if}

          <!-- IDENTIFIERS -->
          <section class="rounded-xl border border-[var(--color-border-3)] bg-white p-5">
            <div class="text-[13px] font-bold mb-3">Ідентифікатори процесора</div>
            <div class="space-y-2 text-xs">
              <div class="flex justify-between"><span class="text-[var(--color-text-muted)]">Order ID</span><strong class="font-mono">{p.externalRef ?? '—'}</strong></div>
              <div class="flex justify-between"><span class="text-[var(--color-text-muted)]">Processor payment ID</span><strong class="font-mono">{p.processorPaymentId ?? '—'}</strong></div>
              <div class="flex justify-between"><span class="text-[var(--color-text-muted)]">Merchant</span><strong>{p.merchantName ?? '—'}{p.merchantId ? ' · ' + p.merchantId : ''}</strong></div>
              <div class="flex justify-between"><span class="text-[var(--color-text-muted)]">Status raw (з CSV)</span><strong class="font-mono">{p.rawStatus ?? '—'}</strong></div>
            </div>
          </section>

          <!-- RAW DATA -->
          {#if p.rawData || p.meta}
            <section class="rounded-xl border border-[var(--color-border-3)] bg-white p-5">
              <div class="flex items-center justify-between mb-2">
                <div class="text-[13px] font-bold">Сирі дані</div>
                <button onclick={() => (showRaw = !showRaw)} class="text-[11px] font-semibold text-[var(--color-pu)] hover:underline">
                  {showRaw ? 'Сховати' : 'Показати'}
                </button>
              </div>
              {#if showRaw}
                {#if p.rawData}
                  <div class="text-[10.5px] font-semibold text-[var(--color-text-muted-2)] uppercase tracking-[0.5px] mb-1">CSV row</div>
                  <pre class="rounded-md bg-[var(--color-bg-2)] p-3 text-[10.5px] font-mono overflow-x-auto leading-[1.6]">{JSON.stringify(p.rawData, null, 2)}</pre>
                {/if}
                {#if p.meta}
                  <div class="mt-3 text-[10.5px] font-semibold text-[var(--color-text-muted-2)] uppercase tracking-[0.5px] mb-1">Meta</div>
                  <pre class="rounded-md bg-[var(--color-bg-2)] p-3 text-[10.5px] font-mono overflow-x-auto leading-[1.6]">{JSON.stringify(p.meta, null, 2)}</pre>
                {/if}
              {/if}
            </section>
          {/if}
        </div>

        <!-- SIDEBAR -->
        <div class="space-y-3">
          {#if p.person}
            {@const person = p.person}
            <a href="#/persons/{person.id}" class="block rounded-xl border border-[var(--color-border-3)] bg-white p-4 no-underline hover:border-[var(--color-pu-2)]">
              <div class="text-[10px] font-semibold uppercase tracking-[1.2px] text-[var(--color-text-muted-2)] mb-2">Person (canonical)</div>
              <div class="flex items-center gap-2.5 mb-2.5">
                <Avatar name={person.displayName ?? payerName} />
                <div class="min-w-0">
                  <div class="text-sm font-semibold truncate">{person.displayName ?? payerName ?? '— без імені —'}</div>
                  <div class="text-[10.5px] text-[var(--color-text-muted)] font-mono truncate">p-{person.id.slice(0, 8)}</div>
                </div>
              </div>
              <div class="space-y-1 text-[11px]">
                <div class="flex justify-between"><span class="text-[var(--color-text-muted)]">LTV</span><strong>{fmtUah(person.ltvCents)}</strong></div>
                <div class="flex justify-between"><span class="text-[var(--color-text-muted)]">Платежів</span><strong>{fmtInt(person.paymentCount)}</strong></div>
              </div>
              <div class="mt-2.5 text-right text-[11px] font-semibold text-[var(--color-pu)]">Профіль →</div>
            </a>
          {:else}
            <div class="rounded-xl border border-dashed border-[var(--color-border-3)] bg-[var(--color-bg-3)] p-4 text-[11px] text-[var(--color-text-muted)]">
              Контакт видалено — платіж відв'язаний.
            </div>
          {/if}

          <div class="rounded-xl border border-[var(--color-border-3)] bg-white p-4">
            <div class="text-[10px] font-semibold uppercase tracking-[1.2px] text-[var(--color-text-muted-2)] mb-2">Provenance</div>
            <div class="space-y-1 text-[11px]">
              <div class="flex justify-between"><span class="text-[var(--color-text-muted)]">Джерело</span><strong class="font-mono text-[10.5px] text-right truncate max-w-[140px]">{p.importedFrom ?? 'webhook'}</strong></div>
              <div class="flex justify-between"><span class="text-[var(--color-text-muted)]">Inferred</span><strong>{p.inferred ? 'так' : 'ні'}</strong></div>
            </div>
          </div>

          <!-- Custom-поля payments (schema-driven через InlineFieldEdit).
               Groups з catalog: admin перейменував/перетягнув групу у Settings →
               тут одразу оновиться. Sort: CustomFieldGroup.position. -->
          {#if customFieldDefs.length > 0}
            <div class="rounded-xl border border-[var(--color-border-3)] bg-white">
              <div class="px-4 py-3 border-b border-[var(--color-border-3)] flex items-center justify-between">
                <span class="text-[10.5px] font-bold uppercase tracking-wider text-[var(--color-text-muted-2)]">Атрибути</span>
                <a
                  href="#/settings/data-structure/tables/payments"
                  class="text-[10px] text-[var(--color-pu-3)] hover:underline"
                  title="Налаштувати поля"
                >налаштувати</a>
              </div>
              {#each groupedCustomFields as g, gIdx (g.label)}
                <div class="px-4 py-3 {gIdx === 0 ? '' : 'border-t border-[var(--color-border-3)]'}">
                  <div class="text-[9.5px] font-bold uppercase tracking-wider text-[var(--color-text-muted-2)] mb-2">{g.label}</div>
                  {#each g.fields as f (f.id)}
                    <InlineFieldEdit
                      field={f}
                      value={p.customFields?.[f.fieldKey]}
                      onSave={(v) => saveTypedCustomField(f.fieldKey, v)}
                      readOnly={!isAdmin}
                      record={{ ...(p as unknown as Record<string, unknown>), ...(p.customFields ?? {}) }}
                    />
                  {/each}
                </div>
              {/each}
            </div>
          {/if}

          <!-- Reverse linked-records: кастомні записи, які посилаються на цей
               платіж (наприклад, "Refund tickets" → "Payment"). Phase 2 V6. -->
          <ReverseLinks sourceSlug="payments" sourceId={p.id} />
        </div>
      </div>
    {/if}
  </div>
</AppShell>
